/* tslint:disable:directive-selector */
import {isPlatformServer} from '@angular/common';
import {
  AfterContentInit,
  Component,
  ContentChildren,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  QueryList,
  SimpleChanges
} from '@angular/core';
import {Subscription} from 'rxjs';
import {
  ExgMapGoogleAPIWrapper,
  ExgMapFitBoundsService,
  ExgMapMarkerManagerService,
  ExgMapInfoWindowManagerService,
  ExgMapCircleManagerService,
  ExgMapRectangleManagerService,
  ExgMapPolylineManagerService,
  ExgMapPolygonManagerService,
  ExgMapLayerManagerService,
  ExgMapDataLayerManagerService,
  ExgMapHeatmapLayerManagerService

} from '../services';

export type ControlPosition = keyof typeof google.maps.ControlPosition;

@Directive()
export abstract class ExgMapControlDirective {
  @Input() position: ControlPosition | undefined;

  abstract getOptions(): Partial<google.maps.MapOptions>;
}

@Directive({

  selector: 'exg-map exg-map-fullscreen-control',
  providers: [{provide: ExgMapControlDirective, useExisting: ExgMapFullscreenControlDirective}],
})
export class ExgMapFullscreenControlDirective extends ExgMapControlDirective {
  getOptions(): Partial<google.maps.MapOptions> {
    return {
      fullscreenControl: true,
      fullscreenControlOptions: {
        position: this.position && google.maps.ControlPosition[this.position],
      },
    };
  }
}

@Directive({
  selector: 'exg-map exg-map-type-control',
  providers: [{provide: ExgMapControlDirective, useExisting: ExgMapTypeControlDirective}],
})
export class ExgMapTypeControlDirective extends ExgMapControlDirective {
  @Input() mapTypeIds: (keyof typeof google.maps.MapTypeId)[] | undefined;
  @Input() style: keyof typeof google.maps.MapTypeControlStyle | undefined;

  getOptions(): Partial<google.maps.MapOptions> {
    return {
      mapTypeControl: true,
      mapTypeControlOptions: {
        position: this.position && google.maps.ControlPosition[this.position],
        style: this.style && google.maps.MapTypeControlStyle[this.style],
        mapTypeIds: this.mapTypeIds && this.mapTypeIds.map(mapTypeId => google.maps.MapTypeId[mapTypeId]),
      },
    };
  }
}

@Directive({
  selector: 'exg-map exg-map-pan-control',
  providers: [{provide: ExgMapControlDirective, useExisting: ExgMapPanControlDirective}],
})
export class ExgMapPanControlDirective extends ExgMapControlDirective {
  getOptions(): Partial<google.maps.MapOptions> {
    return {
      panControl: true,
      panControlOptions: {
        position: this.position && google.maps.ControlPosition[this.position],
      },
    };
  }
}

@Directive({
  selector: 'exg-map exg-map-rotate-control',
  providers: [{provide: ExgMapControlDirective, useExisting: ExgMapRotateControlDirective}],
})
export class ExgMapRotateControlDirective extends ExgMapControlDirective {
  getOptions(): Partial<google.maps.MapOptions> {
    return {
      rotateControl: true,
      rotateControlOptions: {
        position: this.position && google.maps.ControlPosition[this.position],
      },
    };
  }
}

@Directive({
  selector: 'exg-map exg-map-scale-control',
  providers: [{provide: ExgMapControlDirective, useExisting: ExgMapScaleControlDirective}],
})
export class ExgMapScaleControlDirective extends ExgMapControlDirective {
  getOptions(): Partial<google.maps.MapOptions> {
    return {
      scaleControl: true,
    };
  }
}

@Directive({
  selector: 'exg-map exg-map-street-view-control',
  providers: [{provide: ExgMapControlDirective, useExisting: ExgMapStreetViewControlDirective}],
})
export class ExgMapStreetViewControlDirective extends ExgMapControlDirective {
  getOptions(): Partial<google.maps.MapOptions> {
    return {
      streetViewControl: true,
      streetViewControlOptions: {
        position: this.position && google.maps.ControlPosition[this.position],
      },
    };
  }
}

@Directive({
  selector: 'exg-map exg-map-zoom-control',
  providers: [{provide: ExgMapControlDirective, useExisting: ExgMapZoomControlDirective}],
})
export class ExgMapZoomControlDirective extends ExgMapControlDirective {
  @Input() style: any;

  getOptions(): Partial<google.maps.MapOptions> {
    return {
      zoomControl: true,
      zoomControlOptions: {
        position: this.position && google.maps.ControlPosition[this.position]
      },
    };
  }
}


@Component({
  selector: 'exg-map',
  providers: [
    ExgMapFitBoundsService,
    ExgMapGoogleAPIWrapper,
    ExgMapMarkerManagerService,
    ExgMapInfoWindowManagerService,
    ExgMapCircleManagerService,
    ExgMapRectangleManagerService,
    ExgMapPolylineManagerService,
    ExgMapPolygonManagerService,
    ExgMapLayerManagerService,
    ExgMapDataLayerManagerService,
    ExgMapHeatmapLayerManagerService
  ],
  template: `
    <div class='exg-map-container-inner exg-map-container-inner-custom'></div>
    <div class='exg-map-content'>
      <ng-content></ng-content>
    </div>
  `,
  styles: [`
    .exg-map-container-inner {
      width: 100%;
      height: 100%;
    }

    :host.hideprints ::ng-deep .gmnoprint {
      display: none !important;
    }

    :host.hidelogo ::ng-deep div a[rel='noopener'] {
      display: none !important;
    }

    .exg-map-content {
      display: none;
    }
  `]
})
export class ExgMapComponent implements OnChanges, AfterContentInit, OnDestroy {

  /**
   * Map attributes that can change over time
   */
  private static _mapOptionsAttributes: string[] = [
    'disableDoubleClickZoom', 'scrollwheel', 'draggable', 'draggableCursor', 'draggingCursor',
    'keyboardShortcuts', 'styles', 'zoom', 'minZoom', 'maxZoom', 'mapTypeId', 'clickableIcons',
    'gestureHandling', 'tilt', 'restriction',
  ];
  /**
   * Longitude for map center
   */
  @Input() longitude = 0;

  /**
   * Latitude for map center.
   */
  @Input() latitude = 0;

  /**
   * Zoomlevel for map. Default: 8.
   */
  @Input() zoom = 8;

  /**
   * Minimum zoom level allowed for map. (optional) default: no limit
   */
  @Input() minZoom: number | undefined;

  /**
   * Maximum zoom level allowed for map. (optional) default: no limit
   */
  @Input() maxZoom: number | undefined;

  /**
   * The control size for the default map controls. Only governs the controls made by the Maps API itself
   */
  @Input() controlSize: number | undefined;

  /**
   * Enables/disables if map is draggable.
   */
    // tslint:disable-next-line:no-input-rename
  @Input('mapDraggable') draggable = true;

  /**
   * Disable zoom on double click. Default: false (allowed)
   */
  @Input() disableDoubleClickZoom = false;

  /**
   * Enables/disables all default UI of the Google map. Please note: When the map is created, this
   * value cannot get updated.
   */
  @Input() disableDefaultUI = false;

  /**
   * If false, disables scrollwheel zooming on the map. The scrollwheel is enabled by default.
   */
  @Input() scrollwheel = true;

  /**
   * Color used for the background of the Map div. This color will be visible when tiles have not
   * yet loaded as the user pans. This option can only be set when the map is initialized.
   */
  @Input() backgroundColor: string | undefined;

  /**
   * The name or url of the cursor to display when mousing over a draggable map. This property uses
   * the css  * cursor attribute to change the icon. As with the css property, you must specify at
   * least one fallback cursor that is not a URL. For example:
   * [draggableCursor]="'url(http://www.example.com/icon.png), auto;'"
   */
  @Input() draggableCursor: string | undefined;

  /**
   * The name or url of the cursor to display when the map is being dragged. This property uses the
   * css cursor attribute to change the icon. As with the css property, you must specify at least
   * one fallback cursor that is not a URL. For example:
   * [draggingCursor]="'url(http://www.example.com/icon.png), auto;'"
   */
  @Input() draggingCursor: string | undefined;

  /**
   * If false, prevents the map from being controlled by the keyboard. Keyboard shortcuts are
   * enabled by default.
   */
  @Input() keyboardShortcuts = true;

  /**
   * Styles to apply to each of the default map types. Note that for Satellite/Hybrid and Terrain
   * modes, these styles will only apply to labels and geometry.
   */
  @Input() styles: google.maps.MapTypeStyle[] = [];

  /**
   * When true and the latitude and/or longitude values changes, the Google Maps panTo method is
   * used to
   * center the map. See: https://developers.google.com/maps/documentation/javascript/reference#Map
   */
  @Input() usePanning = false;

  /**
   * Sets the viewport to contain the given bounds.
   * If this option to `true`, the bounds get automatically computed from all elements that use the {@link ExgMapFitBoundsService}
   * directive.
   */
  @Input() fitBounds: google.maps.LatLngBoundsLiteral | google.maps.LatLngBounds | boolean = false;

  /**
   * Padding amount for the bounds.
   */
  @Input() fitBoundsPadding: number | google.maps.Padding | undefined;

  /**
   * The map mapTypeId. Defaults to 'roadmap'.
   */
  @Input() mapTypeId: keyof typeof google.maps.MapTypeId = 'ROADMAP';

  /**
   * When false, map icons are not clickable. A map icon represents a point of interest,
   * also known as a POI. By default map icons are clickable.
   */
  @Input() clickableIcons = true;

  /**
   * A map icon represents a point of interest, also known as a POI.
   * When map icons are clickable by default, an info window is displayed.
   * When this property is set to false, the info window will not be shown but the click event
   * will still fire
   */
  @Input() showDefaultInfoWindow = true;

  /**
   * This setting controls how gestures on the map are handled.
   * Allowed values:
   * - 'cooperative' (Two-finger touch gestures pan and zoom the map. One-finger touch gestures are not handled by the map.)
   * - 'greedy'      (All touch gestures pan or zoom the map.)
   * - 'none'        (The map cannot be panned or zoomed by user gestures.)
   * - 'auto'        [default] (Gesture handling is either cooperative or greedy, depending on whether the page is scrollable or not.
   */
  @Input() gestureHandling: string | null | undefined = 'auto';

  /**
   * Controls the automatic switching behavior for the angle of incidence of
   * the map. The only allowed values are 0 and 45. The value 0 causes the map
   * to always use a 0° overhead view regardless of the zoom level and
   * viewport. The value 45 causes the tilt angle to automatically switch to
   * 45 whenever 45° imagery is available for the current zoom level and
   * viewport, and switch back to 0 whenever 45° imagery is not available
   * (this is the default behavior). 45° imagery is only available for
   * satellite and hybrid map types, within some locations, and at some zoom
   * levels. Note: getTilt returns the current tilt angle, not the value
   * specified by this option. Because getTilt and this option refer to
   * different things, do not bind() the tilt property; doing so may yield
   * unpredictable effects. (Default of ExgMap is 0 (disabled). Enable it with value 45.)
   */
  @Input() tilt = 0;

  /**
   * Options for restricting the bounds of the map.
   * User cannot pan or zoom away from restricted area.
   */
  @Input() restriction: google.maps.MapRestriction | undefined;

  private _observableSubscriptions: Subscription[] = [];
  private _fitBoundsSubscription: Subscription | undefined;

  /**
   * This event emitter gets emitted when the user clicks on the map (but not when they click on a
   * marker or infoWindow).
   */
    // tslint:disable-next-line: max-line-length
  @Output() mapClick: EventEmitter<google.maps.MapMouseEvent | google.maps.IconMouseEvent> = new EventEmitter<google.maps.MapMouseEvent | google.maps.IconMouseEvent>();

  /**
   * This event emitter gets emitted when the user right-clicks on the map (but not when they click
   * on a marker or infoWindow).
   */
  @Output() mapRightClick: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter<google.maps.MapMouseEvent>();

  /**
   * This event emitter gets emitted when the user double-clicks on the map (but not when they click
   * on a marker or infoWindow).
   */
  @Output() mapDblClick: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter<google.maps.MapMouseEvent>();

  /**
   * This event emitter is fired when the map center changes.
   */
  @Output() centerChange: EventEmitter<google.maps.LatLngLiteral> = new EventEmitter<google.maps.LatLngLiteral>();

  /**
   * This event is fired when the viewport bounds have changed.
   */
  @Output() boundsChange: EventEmitter<google.maps.LatLngBounds> = new EventEmitter<google.maps.LatLngBounds>();

  /**
   * This event is fired when the mapTypeId property changes.
   */
  @Output() mapTypeIdChange: EventEmitter<string> = new EventEmitter<string>();

  /**
   * This event is fired when the map becomes idle after panning or zooming.
   */
  @Output() idle: EventEmitter<void> = new EventEmitter<void>();

  /**
   * This event is fired when the zoom level has changed.
   */
  @Output() zoomChange: EventEmitter<number> = new EventEmitter<number>();

  /**
   * This event is fired when the google map is fully initialized.
   * You get the google.maps.Map instance as a result of this EventEmitter.
   */
  @Output() mapReady: EventEmitter<any> = new EventEmitter<any>();

  /**
   * This event is fired when the visible tiles have finished loading.
   */
  @Output() tilesLoaded: EventEmitter<void> = new EventEmitter<void>();

  @ContentChildren(ExgMapControlDirective) mapControls: QueryList<ExgMapControlDirective> | undefined;

  constructor(
    private _elem: ElementRef,
    private _mapsWrapper: ExgMapGoogleAPIWrapper,
    // tslint:disable-next-line: ban-types
    @Inject(PLATFORM_ID) private _platformId: Object,
    protected _fitBoundsService: ExgMapFitBoundsService,
    private _zone: NgZone
  ) {
  }

  /** @internal */
  ngAfterContentInit(): void {
    if (isPlatformServer(this._platformId)) {
      // The code is running on the server, do nothing
      return;
    }
    // todo: this should be solved with a new component and a viewChild decorator
    const container = this._elem.nativeElement.querySelector('.exg-map-container-inner');
    this._initMapInstance(container);
  }

  private _initMapInstance(el: HTMLElement): void {
    this._mapsWrapper.createMap(el, {
      center: {lat: this.latitude || 0, lng: this.longitude || 0},
      zoom: this.zoom,
      minZoom: this.minZoom,
      maxZoom: this.maxZoom,
      controlSize: this.controlSize,
      disableDefaultUI: this.disableDefaultUI,
      disableDoubleClickZoom: this.disableDoubleClickZoom,
      scrollwheel: this.scrollwheel,
      backgroundColor: this.backgroundColor,
      draggable: this.draggable,
      draggableCursor: this.draggableCursor,
      draggingCursor: this.draggingCursor,
      keyboardShortcuts: this.keyboardShortcuts,
      styles: this.styles,
      mapTypeId: this.mapTypeId.toLocaleLowerCase(),
      clickableIcons: this.clickableIcons,
      gestureHandling: this.gestureHandling,
      tilt: this.tilt,
      restriction: this.restriction,
    })
      .then(() => this._mapsWrapper.getNativeMap())
      .then(map => {
        this.mapReady.emit(map);
      });

    // register event listeners
    this._handleMapCenterChange();
    this._handleMapZoomChange();
    this._handleMapMouseEvents();
    this._handleBoundsChange();
    this._handleMapTypeIdChange();
    this._handleTilesLoadedEvent();
    this._handleIdleEvent();
    this._handleControlChange();
  }

  getAllChildren = (htmlElement: any) => {
    if (htmlElement.children.length === 0) {
      return [htmlElement];
    }

    const allChildElements = [];

    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < htmlElement.children.length; i++) {
      const children: any = this.getAllChildren(htmlElement.children[i]);
      if (children) {
        allChildElements.push(...children);
      }
    }
    allChildElements.push(htmlElement);

    return allChildElements;
  }

  /** @internal */
  ngOnDestroy(): void {
    // unsubscribe all registered observable subscriptions
    this._observableSubscriptions.forEach((s) => s.unsubscribe());

    // remove all listeners from the map instance
    this._mapsWrapper.clearInstanceListeners();
    if (this._fitBoundsSubscription) {
      this._fitBoundsSubscription.unsubscribe();
    }
  }

  /* @internal */
  ngOnChanges(changes: SimpleChanges): void {
    this._updateMapOptionsChanges(changes);
    this._updatePosition(changes);
  }

  private _updateMapOptionsChanges(changes: SimpleChanges): void {
    const options: { [propName: string]: any } = {};
    const optionKeys =
      Object.keys(changes).filter(k => ExgMapComponent._mapOptionsAttributes.indexOf(k) !== -1);
    optionKeys.forEach((k) => {
      options[k] = changes[k].currentValue;
    });
    this._mapsWrapper.setMapOptions(options);
  }

  /**
   * Triggers a resize event on the google map instance.
   * When recenter is true, the of the google map gets called with the current lat/lng values or fitBounds value to recenter the map.
   * Returns a promise that gets resolved after the event was triggered.
   */
  triggerResize(recenter: boolean = true): Promise<void> {
    // Note: When we would trigger the resize event and show the map in the same turn (which is a
    // common case for triggering a resize event), then the resize event would not
    // work (to show the map), so we trigger the event in a timeout.
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        return this._mapsWrapper.triggerMapEvent('resize').then(() => {
          if (recenter) {
            this.fitBounds != null ? this._fitBounds() : this._setCenter();
          }
          resolve();
        });
      });
    });
  }

  private _updatePosition(changes: SimpleChanges): void {
    // tslint:disable: no-string-literal
    if (changes['latitude'] == null && changes['longitude'] == null &&
      !changes['fitBounds']) {
      // no position update needed
      return;
    }
    // tslint:enable: no-string-literal

    // we prefer fitBounds in changes
    if ('fitBounds' in changes) {
      this._fitBounds();
      return;
    }

    if (typeof this.latitude !== 'number' || typeof this.longitude !== 'number') {
      return;
    }
    this._setCenter();
  }

  private _setCenter(): void {
    const newCenter = {
      lat: this.latitude,
      lng: this.longitude,
    };
    if (this.usePanning) {
      this._mapsWrapper.panTo(newCenter);
    } else {
      this._mapsWrapper.setCenter(newCenter);
    }
  }

  private _fitBounds(): void {
    switch (this.fitBounds) {
      case true:
        this._subscribeToFitBoundsUpdates();
        break;
      case false:
        if (this._fitBoundsSubscription) {
          this._fitBoundsSubscription.unsubscribe();
        }
        break;
      default:
        if (this._fitBoundsSubscription) {
          this._fitBoundsSubscription.unsubscribe();
        }
        this._updateBounds(this.fitBounds, this.fitBoundsPadding);
    }
  }

  private _subscribeToFitBoundsUpdates(): void {
    this._zone.runOutsideAngular(() => {
      this._fitBoundsSubscription = this._fitBoundsService.getBounds$().subscribe(b => {
        this._zone.run(() => this._updateBounds(b, this.fitBoundsPadding));
      });
    });
  }

  protected _updateBounds(
    bounds: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral,
    padding?: number | google.maps.Padding): void {
    if (!bounds) {
      return;
    }
    if (this._isLatLngBoundsLiteral(bounds) && typeof google !== 'undefined' && google && google.maps && google.maps.LatLngBounds) {
      const newBounds = new google.maps.LatLngBounds();
      newBounds.union(bounds);
      bounds = newBounds;
    }
    if (this.usePanning) {
      this._mapsWrapper.panToBounds(bounds, padding);
      return;
    }
    this._mapsWrapper.fitBounds(bounds, padding);
  }

  private _isLatLngBoundsLiteral(bounds: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral): boolean {
    return bounds != null && (bounds as any).extend === undefined;
  }

  private _handleMapCenterChange(): void {
    const s = this._mapsWrapper.subscribeToMapEvent('center_changed').subscribe(() => {
      this._mapsWrapper.getCenter().then((center: google.maps.LatLng | undefined) => {
        this.latitude = center?.lat() || 0;
        this.longitude = center?.lng() || 0;
        this.centerChange.emit({lat: this.latitude, lng: this.longitude} as google.maps.LatLngLiteral);
      });
    });
    this._observableSubscriptions.push(s);
  }

  private _handleBoundsChange(): void {
    const s = this._mapsWrapper.subscribeToMapEvent('bounds_changed').subscribe(() => {

      this._mapsWrapper.getBounds().then(
        // @ts-ignore
        (bounds: google.maps.LatLngBounds) => {
          this.boundsChange.emit(bounds);
        });
    });
    this._observableSubscriptions.push(s);
  }

  private _handleMapTypeIdChange(): void {
    const s = this._mapsWrapper.subscribeToMapEvent('maptypeid_changed').subscribe(() => {
      this._mapsWrapper.getMapTypeId().then(
        (mapTypeId: string | undefined) => {
          this.mapTypeIdChange.emit(mapTypeId);
        });
    });
    this._observableSubscriptions.push(s);
  }

  private _handleMapZoomChange(): void {
    const s = this._mapsWrapper.subscribeToMapEvent('zoom_changed').subscribe(() => {
      this._mapsWrapper.getZoom().then((z: number | undefined) => {
        this.zoom = z || this.zoom;
        this.zoomChange.emit(z);
      });
    });
    this._observableSubscriptions.push(s);
  }

  private _handleIdleEvent(): void {
    const s = this._mapsWrapper.subscribeToMapEvent('idle').subscribe(
      () => {
        this.idle.emit(void 0);
      });
    this._observableSubscriptions.push(s);
  }

  private _handleTilesLoadedEvent(): void {
    const s = this._mapsWrapper.subscribeToMapEvent('tilesloaded').subscribe(
      () => this.tilesLoaded.emit(void 0),
    );
    this._observableSubscriptions.push(s);
  }

  private _handleMapMouseEvents(): void {
    type Event = { name: 'rightclick' | 'click' | 'dblclick', emitter: EventEmitter<google.maps.MapMouseEvent> };

    const events: Event[] = [
      {name: 'click', emitter: this.mapClick},
      {name: 'rightclick', emitter: this.mapRightClick},
      {name: 'dblclick', emitter: this.mapDblClick},
    ];

    events.forEach(e => {
      const s = this._mapsWrapper.subscribeToMapEvent(e.name).subscribe(
        // @ts-ignore
        ([event]) => {
          // the placeId will be undefined in case the event was not an IconMouseEvent (google types)
          if ((event as google.maps.IconMouseEvent).placeId && !this.showDefaultInfoWindow) {
            event.stop();
          }
          e.emitter.emit(event);
        });
      this._observableSubscriptions.push(s);
    });
  }

  _handleControlChange(): void {
    this._setControls();
    this.mapControls?.changes.subscribe(() => this._setControls());
  }

  _setControls(): void {
    const controlOptions: Partial<google.maps.MapOptions> = {
      fullscreenControl: !this.disableDefaultUI,
      mapTypeControl: false,
      panControl: false,
      rotateControl: false,
      scaleControl: false,
      streetViewControl: !this.disableDefaultUI,
      zoomControl: !this.disableDefaultUI,
    };

    if (typeof google === 'object' && typeof google.maps === 'object') {
      this.mapControls?.forEach(control => Object.assign(controlOptions, control.getOptions()));
    } else {
      const un = this.mapReady.subscribe(() => {
        if (typeof google === 'object' && typeof google.maps === 'object') {
          this.mapControls?.forEach(control => Object.assign(controlOptions, control.getOptions()));
        }
        this._mapsWrapper.setMapOptions(controlOptions);
        un.unsubscribe();
      });
    }
    this._mapsWrapper.setMapOptions(controlOptions);
  }
}
